import { useEffect, useState } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { Gradients, Header, ResultsPage, StarIcon } from "@datagame/ui";
import { useTranslation } from "react-i18next";

import { Transition } from "../shared/Transition";
import { NumberCounter } from "./NumberCounter";
import { GameResultsConfiguration } from "../../gql/graphql";
import { GAME_RESULTS_ANIMATION_DURATION } from "../../constants";
import { CustomizedBackground } from "../CustomizedBackground";

import styles from "./GameResults.module.scss";

export interface GameResultsProps {
  result: number;
  onConfirm: () => void;
  config: GameResultsConfiguration;
  themeColor: string;
}

export const GameResults = ({ result, themeColor, onConfirm, config }: GameResultsProps) => {
  const { t } = useTranslation();

  const [showResults, setShowResults] = useState(false);

  const resultInPx = result * 4;

  const height = useMotionValue(0);

  const width = useMotionValue(0);

  const background = useTransform(
    height,
    [0, 80, 80, 160, 160, 240, 240, 320, 320, 400],
    [
      Gradients.Neutrals,
      Gradients.Neutrals,
      Gradients.Blue,
      Gradients.Blue,
      Gradients.Green,
      Gradients.Green,
      Gradients.Orange,
      Gradients.Orange,
      Gradients.Red,
      Gradients.Red,
    ],
  );

  useEffect(() => {
    const timeId = setTimeout(() => {
      setShowResults(true);
    }, 4500);

    return () => {
      clearTimeout(timeId);
    };
  }, []);

  const renderContent = () => {
    if (showResults) {
      return <ResultsPage config={config} result={result} onConfirm={onConfirm} themeColor={themeColor} />;
    }
    return (
      <div className={styles.container}>
        <Header variant="h6" className={styles.header}>
          {t("gameResults:tracking")}
        </Header>
        <div className={styles.resultCard}>
          <motion.div
            className={styles.progressStatus}
            style={{ height, background }}
            animate={{
              transition: { duration: GAME_RESULTS_ANIMATION_DURATION },
              height: `${resultInPx}px`,
            }}
          />
          <StarIcon className={styles.star} />
        </div>

        <div className={styles.progressWrapper}>
          <motion.div
            className={styles.progress}
            style={{ width, background }}
            animate={{
              transition: { duration: GAME_RESULTS_ANIMATION_DURATION },
              width: `${result}%`,
            }}
          />
        </div>
        <NumberCounter to={result} />
      </div>
    );
  };

  return (
    <Transition>
      <CustomizedBackground config={config}>{renderContent()}</CustomizedBackground>
    </Transition>
  );
};
